import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../utils/api";
import { buildImageUrl } from "../utils/imageUtils";
import { QUERY_KEYS } from "./usePrefetchStoreData";

type Service = {
  id: number;
  name: string;
  image?: string;
  category: number;
  is_quantity_requestable?: boolean;
  prices: {
    id: number;
    size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
    final_price: number;
  }[];
};

type PaginatedServiceResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Service[];
};

export function useServices(page: number = 1) {
  return useQuery<PaginatedServiceResponse>({
    queryKey: ["services", page],
    queryFn: async () => {
      const data = await fetcher<PaginatedServiceResponse>(
        `/store/services/?page=${page}&page_limit=100`
      );

      // معالجة URLs الصور وإنشاء URLs كاملة
      const servicesWithFixedImages = data.results.map((service) => ({
        ...service,
        image: (buildImageUrl(service.image ?? null) ?? undefined) as
          | string
          | undefined,
      }));

      return {
        ...data,
        results: servicesWithFixedImages,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 دقائق للبيانات شبه الثابتة
    gcTime: 30 * 60 * 1000, // 30 دقيقة
  });
}

// للاستخدام في أماكن أخرى بدون pagination - يجلب جميع الصفحات
export function useAllServices() {
  return useQuery<Service[]>({
    queryKey: QUERY_KEYS.servicesAll,
    queryFn: async () => {
      const allServices: Service[] = [];
      let nextUrl: string | null = "/store/services/?page_limit=100";

      // جلب جميع الصفحات
      while (nextUrl) {
        const data: PaginatedServiceResponse = await fetcher<PaginatedServiceResponse>(nextUrl);
        allServices.push(...data.results);

        // استخراج الـ path من next URL إذا كان موجوداً
        if (data.next) {
          const url: URL = new URL(data.next);
          nextUrl = url.pathname + url.search;
        } else {
          nextUrl = null;
        }
      }

      // معالجة URLs الصور وإنشاء URLs كاملة
      const servicesWithFixedImages = allServices.map((service) => ({
        ...service,
        image: (buildImageUrl(service.image ?? null) ?? undefined) as
          | string
          | undefined,
      }));

      return servicesWithFixedImages;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useService(id: number | string | null | undefined) {
  return useQuery<Service>({
    queryKey: ["services", id],
    queryFn: async () => {
      const service = await fetcher<Service>(`/store/services/${id}/`);

      return {
        ...service,
        image: (buildImageUrl(service.image ?? null) ?? undefined) as
          | string
          | undefined,
      };
    },
    enabled: !!id && id !== 0 && id !== "0",
  });
}
